import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiSend, FiArrowLeft } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import storageService from '../services/storageService';
import syncService, { SyncStatus } from '../services/syncService';
import { postsAPI, categoriasAPI } from '../utils/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ConfirmationModal from '../components/ConfirmationModal';

/**
 * Página para crear/editar posts
 * - Usuario registrado sin permisos: crea localmente o envía a revisión
 * - Posteador/Admin: crea directamente en BD principal
 */
const PostCrearPage = () => {
  const { id } = useParams(); // Para editar post local
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    contenido: '',
    imagen: '',
    palabrasClave: ''
  });
  const [categorias, setCategorias] = useState([]);
  const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState([]);
  
  // Estados para el modal de confirmación
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    type: 'success',
    redirectOnClose: false // Nueva propiedad para controlar redirección
  });

  // Verificar permisos
  const canPublishDirectly = user?.rol === 'administrador' || user?.modulePermissions?.some(
    p => p.moduleName === 'posts' && p.roleName === 'posteador'
  );

  // Manejar cierre del modal con posible redirección
  const handleCloseModal = () => {
    setShowModal(false);
    if (modalConfig.redirectOnClose) {
      navigate('/estudios');
    }
  };

  useEffect(() => {
    if (id && id.startsWith('local_')) {
      cargarPostLocal(parseInt(id.replace('local_', '')));
    }
    cargarCategorias();
  }, [id]);

  const cargarCategorias = async () => {
    try {
      const response = await categoriasAPI.listar();
      setCategorias(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error cargando categorías:', error);
    }
  };

  const cargarPostLocal = async (localId) => {
    try {
      console.log('🔄 Cargando post local con ID:', localId);
      await storageService.init();
      const post = await storageService.posts.obtenerPorId(localId);
      console.log('📝 Post local cargado:', post);
      
      if (post) {
        const nuevoFormData = {
          titulo: post.titulo || '',
          descripcion: post.descripcion || '',
          contenido: post.contenido || '',
          imagen: post.imagen || '',
          palabrasClave: Array.isArray(post.palabrasClave) 
            ? post.palabrasClave.join(', ')
            : (post.palabrasClave || '')
        };
        
        console.log('📋 Datos del formulario a cargar:', nuevoFormData);
        setFormData(nuevoFormData);
        setCategoriasSeleccionadas(post.categorias || []);
      } else {
        console.warn('⚠️ Post local no encontrado con ID:', localId);
      }
    } catch (error) {
      console.error('❌ Error cargando post local:', error);
      setModalConfig({
        title: '❌ Error al Cargar',
        message: 'No se pudo cargar el post local:\n\n' + error.message,
        type: 'error'
      });
      setShowModal(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const guardarLocalmente = async () => {
    if (!formData.titulo || !formData.contenido) {
      setModalConfig({
        title: '⚠️ Campos Requeridos',
        message: 'El título y el contenido son campos obligatorios.',
        type: 'warning'
      });
      setShowModal(true);
      return;
    }

    setLoading(true);
    try {
      await storageService.init();
      
      const postData = {
        ...formData,
        palabrasClave: formData.palabrasClave.split(',').map(k => k.trim()).filter(Boolean),
        categorias: categoriasSeleccionadas,
        estado: 'borrador_local',
        syncStatus: SyncStatus.LOCAL, // Estado inicial: solo local
        userId: user?.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (id && id.startsWith('local_')) {
        // Actualizar
        const localId = parseInt(id.replace('local_', ''));
        await storageService.posts.actualizar(localId, postData);
      setModalConfig({
        title: '✅ Post Guardado',
        message: 'Tu post ha sido guardado exitosamente en tu navegador.\n\nPuedes continuar editándolo más tarde o enviarlo a revisión cuando esté listo.',
        type: 'success',
        redirectOnClose: true
      });
      } else {
        // Crear
        await storageService.posts.agregar(postData);
        setModalConfig({
          title: '✅ Post Guardado',
          message: 'Tu post ha sido guardado exitosamente en tu navegador.\n\nPuedes continuar editándolo más tarde o enviarlo a revisión cuando esté listo.',
          type: 'success',
          redirectOnClose: true
        });
      }

      setShowModal(true);
      // Esperar a que el usuario cierre el modal manualmente
      // (el modal ya tiene onClose que redirige)
    } catch (error) {
      console.error('Error guardando post localmente:', error);
      setModalConfig({
        title: '❌ Error al Guardar',
        message: 'Ocurrió un error al intentar guardar el post:\n\n' + error.message,
        type: 'error'
      });
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const enviarARevision = async () => {
    // Validaciones de campos requeridos
    if (!formData.titulo || !formData.contenido) {
      setModalConfig({
        title: '⚠️ Campos Requeridos',
        message: 'El título y el contenido son campos obligatorios.',
        type: 'warning'
      });
      setShowModal(true);
      return;
    }

    // Validación de descripción (el backend requiere entre 10 y 5000 caracteres)
    if (!formData.descripcion || formData.descripcion.trim().length < 10) {
      setModalConfig({
        title: '⚠️ Descripción Requerida',
        message: 'La descripción es obligatoria y debe tener al menos 10 caracteres.\n\nActual: ' + (formData.descripcion?.length || 0) + ' caracteres.',
        type: 'warning'
      });
      setShowModal(true);
      return;
    }

    if (formData.descripcion.length > 5000) {
      setModalConfig({
        title: '⚠️ Descripción Demasiado Larga',
        message: 'La descripción no puede exceder los 5000 caracteres.\n\nActual: ' + formData.descripcion.length + ' caracteres.',
        type: 'warning'
      });
      setShowModal(true);
      return;
    }

    if (!confirm('¿Enviar este post a revisión? Un posteador/administrador lo revisará antes de publicarlo.')) {
      return;
    }

    setLoading(true);
    try {
      const postData = {
        titulo: formData.titulo,
        descripcion: formData.descripcion,
        contenido: formData.contenido,
        imagen: formData.imagen,
        palabrasClave: formData.palabrasClave.split(',').map(k => k.trim()).filter(Boolean),
        categorias: categoriasSeleccionadas.map(cat => typeof cat === 'object' ? cat.id : cat),
        publicado: false // Queda como borrador
      };

      // Enviar al backend usando el endpoint de submitPost
      const response = await postsAPI.submitPost(postData);
      const backendPost = response.data.data;

      // Si es un post local, actualizar su estado a PENDING
      if (id && id.startsWith('local_')) {
        const localId = parseInt(id.replace('local_', ''));
        
        // Actualizar el estado de sincronización
        await syncService.updatePostSyncStatus(
          localId, 
          SyncStatus.PENDING, 
          backendPost.id // Guardar ID del backend para sincronización futura
        );
        
        console.log('✅ Post local actualizado a estado PENDING');
      }

      setModalConfig({
        title: '✅ Post Enviado a Revisión',
        message: '¡Tu post ha sido enviado exitosamente!\n\n📝 Estado: Pendiente de aprobación\n👤 Revisor: Posteador/Administrador\n\n🔔 Te notificaremos cuando sea aprobado.',
        type: 'success',
        redirectOnClose: true
      });
      setShowModal(true);
      // Esperar a que el usuario cierre el modal manualmente
    } catch (error) {
      console.error('Error enviando post a revisión:', error);
      
      // Extraer mensaje de error más específico
      let errorMessage = 'Ocurrió un error desconocido';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setModalConfig({
        title: '❌ Error al Enviar',
        message: errorMessage,
        type: 'error'
      });
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const publicarDirectamente = async () => {
    if (!formData.titulo || !formData.contenido) {
      alert('Título y contenido son obligatorios');
      return;
    }

    if (!confirm('¿Publicar este post directamente?')) {
      return;
    }

    setLoading(true);
    try {
      const postData = {
        titulo: formData.titulo,
        descripcion: formData.descripcion,
        contenido: formData.contenido,
        imagen: formData.imagen,
        palabrasClave: formData.palabrasClave.split(',').map(k => k.trim()).filter(Boolean),
        categorias: categoriasSeleccionadas.map(cat => typeof cat === 'object' ? cat.id : cat),
        publicado: true // Publicar directamente
      };

      await postsAPI.crear(postData);
      alert('✅ Post publicado exitosamente');
      navigate('/estudios');
    } catch (error) {
      console.error('Error publicando post:', error);
      alert('Error al publicar el post: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/estudios')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <FiArrowLeft />
              Volver a Posts
            </button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {id ? 'Editar Post Local' : 'Crear Nuevo Post'}
          </h1>
          <p className="text-gray-600 mt-2">
            {canPublishDirectly
              ? '✨ Tienes permisos para publicar directamente'
              : '📝 Puedes guardar localmente o enviar a revisión'}
          </p>
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <form className="space-y-6">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título *
              </label>
              <input
                type="text"
                name="titulo"
                value={formData.titulo}
                onChange={handleChange}
                placeholder="Título del post"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción breve
              </label>
              <textarea
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                placeholder="Breve descripción del post (se mostrará en la tarjeta)"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Contenido */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contenido *
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={formData.contenido}
                  onChange={(value) => setFormData({ ...formData, contenido: value })}
                  placeholder="Escribe el contenido completo del post..."
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'indent': '-1'}, { 'indent': '+1' }],
                      [{ 'align': [] }],
                      ['link', 'image', 'video'],
                      [{ 'color': [] }, { 'background': [] }],
                      ['blockquote', 'code-block'],
                      ['clean']
                    ]
                  }}
                  formats={[
                    'header',
                    'bold', 'italic', 'underline', 'strike',
                    'list', 'bullet', 'indent',
                    'link', 'image', 'video',
                    'align',
                    'color', 'background',
                    'blockquote', 'code-block'
                  ]}
                  style={{ height: '300px', marginBottom: '50px' }}
                />
              </div>
            </div>

            {/* Imagen */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL de Imagen
              </label>
              <input
                type="url"
                name="imagen"
                value={formData.imagen}
                onChange={handleChange}
                placeholder="https://ejemplo.com/imagen.jpg"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Palabras clave */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Palabras Clave
              </label>
              <input
                type="text"
                name="palabrasClave"
                value={formData.palabrasClave}
                onChange={handleChange}
                placeholder="palabra1, palabra2, palabra3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Separar con comas</p>
            </div>

            {/* Categorías */}
            {categorias.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categorías
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-4 border border-gray-300 rounded-lg max-h-48 overflow-y-auto bg-gray-50">
                  {categorias.map((categoria) => {
                    const isSelected = categoriasSeleccionadas.some(
                      c => (typeof c === 'object' ? c.id : c) === categoria.id
                    );
                    return (
                      <label
                        key={categoria.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            if (isSelected) {
                              setCategoriasSeleccionadas(prev => 
                                prev.filter(c => (typeof c === 'object' ? c.id : c) !== categoria.id)
                              );
                            } else {
                              setCategoriasSeleccionadas(prev => [...prev, categoria]);
                            }
                          }}
                          className="rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">{categoria.nombre}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {categoriasSeleccionadas.length} {categoriasSeleccionadas.length === 1 ? 'categoría seleccionada' : 'categorías seleccionadas'}
                </p>
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
              {canPublishDirectly ? (
                // Admin/Posteador: puede publicar directamente
                <>
                  <button
                    type="button"
                    onClick={guardarLocalmente}
                    disabled={loading}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <FiSave className="w-5 h-5" />
                    Guardar como borrador
                  </button>
                  <button
                    type="button"
                    onClick={publicarDirectamente}
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <FiSend className="w-5 h-5" />
                    {loading ? 'Publicando...' : 'Publicar Post'}
                  </button>
                </>
              ) : (
                // Usuario registrado: guarda localmente o envía a revisión
                <>
                  <button
                    type="button"
                    onClick={guardarLocalmente}
                    disabled={loading}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <FiSave className="w-5 h-5" />
                    {loading ? 'Guardando...' : 'Guardar Localmente'}
                  </button>
                  <button
                    type="button"
                    onClick={enviarARevision}
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <FiSend className="w-5 h-5" />
                    {loading ? 'Enviando...' : 'Enviar a Revisión'}
                  </button>
                </>
              )}
            </div>

            {/* Nota informativa */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                {canPublishDirectly ? (
                  <>
                    <strong>💡 Tip:</strong> Como posteador/administrador, puedes publicar directamente o guardar como borrador para revisar más tarde.
                  </>
                ) : (
                  <>
                    <strong>💡 Tip:</strong> Puedes guardar localmente para continuar editando más tarde, o enviar a revisión cuando esté listo. Los posts enviados a revisión no podrán ser editados hasta que sean publicados o rechazados.
                  </>
                )}
              </p>
            </div>
          </form>
        </div>
      </div>
      
      {/* Modal de confirmación */}
      <ConfirmationModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
    </div>
  );
};

export default PostCrearPage;

